import { IANA_TLDS } from './tlds';

/**
 * Validación de email común a todos los repos Yulava (SPEC.md).
 * Función pura, sin consultas DNS: el MX lo comprueba el backend en el envío.
 */
export type EmailCheck =
  | { valid: true; normalized: string; suggestion?: string }
  | { valid: false; reason: 'syntax' | 'tld' | 'provider'; suggestion?: string };

export const POPULAR_EMAIL_DOMAINS: readonly string[] = [
  'gmail.com', 'hotmail.com', 'hotmail.es', 'outlook.com', 'outlook.es', 'yahoo.com', 'yahoo.es',
  'icloud.com', 'live.com', 'live.es', 'msn.com', 'me.com', 'googlemail.com', 'protonmail.com',
  'proton.me', 'telefonica.net', 'movistar.es', 'orange.es', 'vodafone.es', 'ono.com', 'terra.es',
  'gmx.com', 'gmx.es', 'aol.com', 'yandex.com',
];

/** Proveedores de un solo dominio: nombre -> dominio canónico. */
const SINGLE_DOMAIN_PROVIDERS: { [name: string]: string } = {
  gmail: 'gmail.com',
  googlemail: 'googlemail.com',
  icloud: 'icloud.com',
};

const LABEL_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const TLD_RE = /^[a-z]{2,}$/;
const MIN_DOMAIN_LENGTH_FOR_SUGGESTION = 5;
const MAX_SUGGESTION_DISTANCE = 2;

export function levenshtein(a: string, b: string): number {
  let prev: number[] = [];
  for (let j = 0; j <= b.length; j++) {
    prev.push(j);
  }
  for (let i = 1; i <= a.length; i++) {
    const cur: number[] = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
      cur.push(Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost));
    }
    prev = cur;
  }
  return prev[b.length];
}

/** Dominio popular más cercano (distancia <= 2); en empate, el primero de la lista. */
function closestPopularDomain(domain: string): string | undefined {
  if (domain.length < MIN_DOMAIN_LENGTH_FOR_SUGGESTION || POPULAR_EMAIL_DOMAINS.indexOf(domain) !== -1) {
    return undefined;
  }
  let best: string | undefined;
  let bestDistance = MAX_SUGGESTION_DISTANCE + 1;
  for (const popular of POPULAR_EMAIL_DOMAINS) {
    const distance = levenshtein(domain, popular);
    if (distance < bestDistance) {
      best = popular;
      bestDistance = distance;
    }
  }
  return best;
}

function hasValidSyntax(local: string, domain: string, total: number): boolean {
  if (total > 254 || local.length < 1 || local.length > 64 || /[\s@]/.test(local)) {
    return false;
  }
  const labels = domain.split('.');
  if (labels.length < 2 || !labels.every(label => LABEL_RE.test(label))) {
    return false;
  }
  return TLD_RE.test(labels[labels.length - 1]);
}

export function checkEmail(raw: string): EmailCheck {
  const value = (raw || '').trim();
  const parts = value.split('@');
  if (parts.length !== 2) {
    return { valid: false, reason: 'syntax' };
  }
  const [local, domainRaw] = parts;
  const domain = domainRaw.toLowerCase();
  if (!hasValidSyntax(local, domain, value.length)) {
    return { valid: false, reason: 'syntax' };
  }

  const labels = domain.split('.');
  const tld = labels[labels.length - 1];
  const popular = closestPopularDomain(domain);
  const suggestion = popular ? `${local}@${popular}` : undefined;

  if (!IANA_TLDS.has(tld)) {
    return suggestion ? { valid: false, reason: 'tld', suggestion } : { valid: false, reason: 'tld' };
  }

  const providerName = labels.slice(0, -1).join('.');
  const canonical = SINGLE_DOMAIN_PROVIDERS[providerName];
  if (canonical && domain !== canonical) {
    return { valid: false, reason: 'provider', suggestion: suggestion || `${local}@${canonical}` };
  }

  return suggestion ? { valid: true, normalized: value, suggestion } : { valid: true, normalized: value };
}

/** Dominio (en minúsculas) de un email ya recortado; cadena vacía si no hay. */
export function emailDomain(value: string): string {
  const at = (value || '').trim().lastIndexOf('@');
  return at === -1 ? '' : value.trim().slice(at + 1).toLowerCase();
}
